import type React from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '@src/i18n'
import { generatePath } from 'react-router-dom'

import routes from '@src/Routes/routes'
import { CALL_LINK, GUIDES_LINK, YOUTUBE_PLAYLISTS } from '@src/constants'
import { SUPPORT_EMAIL } from '@msteams/constants'
import { isIntercomAvailable, safeIntercom } from '@helpers/Intercom/safe'
import { openSupportChat } from '@helpers/Intercom/support'
import {
  IconCircleCheck,
  IconListDetails,
  IconBolt,
  IconBuildingWarehouse,
  IconUsers,
  IconHome,
  IconUsersGroup,
  IconReceipt,
  IconTruckDelivery,
  IconFileInvoice,
  IconCoins,
  IconMail,
  IconFileDollar,
  IconChartInfographic,
  IconNotes,
  IconFileDescription,
  IconSparkles,
  IconPlug,
} from '@tabler/icons-react'

import { ContactParty } from '@models/ApproveitAccounting'
import { FeatureKey } from '@type/featureKeys'
import { Perm } from '@type/permissions'
import type { PermCodename } from '@type/permissions'

export interface NavItem {
  to?: string
  label: string
  icon: React.FC<Record<string, unknown>>
  /**
   * The backend feature this entry belongs to. The ONLY gate a nav item
   * declares: whether the viewer may use it (plan, Settings → Features
   * toggle, beta flag, permissions) is resolved server side and shipped
   * on /my-account as `features` (one state per key). Both shells' navs
   * AND their route guards read it, so registering a feature is the
   * whole change.
   */
  feature: FeatureKey
  /**
   * Action-level refinement for entries that CREATE something inside a
   * feature (the "+" quick-action menu). The feature answers "may they
   * open Vendors"; this answers "may they add one". Not for tab entries —
   * a tab's permission belongs on its registry entry.
   */
  requirePerm?: PermCodename
  /**
   * Tooltip for a `locked` state that has no paywall behind it (the row
   * stays clickable and the page carries the pitch). Plan locks use the
   * generic upgrade tooltip.
   */
  lockTooltip?: string
  external?: boolean
  onClick?: () => void
  isCollapsed?: boolean
  disabled?: boolean
}

// LEFT ITEMS

export const NAV_ITEMS: NavItem[] = [
  {
    to: routes.private.dashboard,
    icon: IconHome,
    label: i18n.t('common:navigation.dashboard'),
    feature: FeatureKey.dashboard,
  },
  {
    to: routes.private.reports,
    icon: IconChartInfographic,
    label: i18n.t('common:navigation.reports'),
    feature: FeatureKey.reports,
  },
  {
    // Offered to every team; `locked` (dimmed, still clickable) until the
    // chatbot beta flag is on, and the page shows the pitch.
    to: routes.private.assistant,
    icon: IconSparkles,
    label: i18n.t('common:navigation.aiAssistant'),
    feature: FeatureKey.aiAssistant,
    lockTooltip: i18n.t('common:navigation.aiAssistantLockedTooltip'),
  },
  {
    // A container entry (Custom lists is a tab of this section); the
    // destination is resolved per viewer by filterNavItems.
    to: routes.private.pipelines,
    icon: IconBolt,
    label: i18n.t('common:navigation.workflows'),
    feature: FeatureKey.workflows,
  },
  {
    to: routes.private.approvalRequests,
    icon: IconCircleCheck,
    label: i18n.t('common:navigation.approvalRequests'),
    feature: FeatureKey.approvalRequests,
  },
  {
    to: routes.private.actions,
    icon: IconPlug,
    label: i18n.t('common:navigation.actions'),
    feature: FeatureKey.actions,
  },
  {
    to: routes.private.tasks,
    icon: IconListDetails,
    label: i18n.t('common:navigation.tasks'),
    feature: FeatureKey.tasks,
  },
  {
    to: routes.private.mailbox,
    icon: IconMail,
    label: i18n.t('common:navigation.mailbox'),
    feature: FeatureKey.mailbox,
  },
  {
    // A container entry (Members / Departments / Locations / Roles); the
    // /members route (TeamHome) lands the viewer on the first tab they
    // can actually open.
    to: routes.private.organizationalStructure,
    icon: IconUsersGroup,
    label: i18n.t('common:navigation.team'),
    feature: FeatureKey.team,
  },
  {
    to: routes.private.costCenter,
    icon: IconCoins,
    label: i18n.t('common:navigation.costCenters'),
    feature: FeatureKey.costCenters,
  },
]

export const AR_ITEMS: NavItem[] = [
  {
    to: routes.private.invoices,
    icon: IconFileInvoice,
    label: i18n.t('common:navigation.invoices'),
    feature: FeatureKey.invoices,
  },
  {
    to: routes.private.quotes,
    icon: IconFileDollar,
    label: i18n.t('common:navigation.quotes'),
    feature: FeatureKey.quotes,
  },
  {
    to: generatePath(routes.private.contacts, { contactParty: ContactParty.customer }),
    icon: IconUsers,
    label: i18n.t('common:navigation.customers'),
    feature: FeatureKey.customers,
  },
]
export const AP_ITEMS: NavItem[] = [
  {
    to: routes.private.bills,
    icon: IconReceipt,
    label: i18n.t('common:navigation.bills'),
    feature: FeatureKey.bills,
  },
  {
    to: routes.private.purchaseOrders,
    icon: IconTruckDelivery,
    label: i18n.t('common:navigation.purchaseOrders'),
    feature: FeatureKey.purchaseOrders,
  },
  {
    to: routes.private.rfqs,
    icon: IconFileDescription,
    label: i18n.t('common:navigation.rfq'),
    feature: FeatureKey.rfqs,
  },
  {
    to: generatePath(routes.private.contacts, { contactParty: ContactParty.vendor }),
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.vendors'),
    feature: FeatureKey.vendors,
  },
]
export const CONTRACT_ITEMS: NavItem[] = [
  {
    to: routes.private.contracts,
    icon: IconNotes,
    label: i18n.t('common:navigation.contracts'),
    feature: FeatureKey.contracts,
  },
]

export const ORGANIZATION_MENU: NavItem[] = [
  {
    to: routes.private.products,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.products'),
    feature: FeatureKey.products,
  },
  {
    to: routes.private.accounts,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.accounts'),
    feature: FeatureKey.accounts,
  },
  {
    to: routes.private.accountCategories,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.categories'),
    feature: FeatureKey.categories,
  },
  {
    to: routes.private.taxes,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.taxes'),
    feature: FeatureKey.taxes,
  },
  {
    to: routes.private.companyDetails,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.companyDetails'),
    feature: FeatureKey.companyDetails,
  },
]
// HEAD ITEMS

export const QUICK_ACTIONS_ITEMS: NavItem[] = [
  {
    to: routes.private.approvalRequest,
    label: i18n.t('common:navigation.approvalRequest'),
    icon: IconCircleCheck,
    feature: FeatureKey.approvalRequests,
    // The destination route is `requirePerm={Perm.REQUEST_ADD}` —
    // mirror it here so the "+" quick-action menu doesn't surface
    // an entry that redirects to landing.
    requirePerm: Perm.REQUEST_ADD,
  },
  {
    to: routes.private.taskBoards,
    label: i18n.t('common:navigation.task'),
    icon: IconListDetails,
    feature: FeatureKey.tasks,
  },
  {
    to: generatePath(routes.private.pipelineById, { id: 'new' }),
    label: i18n.t('common:navigation.workflow'),
    icon: IconBolt,
    feature: FeatureKey.workflows,
    requirePerm: Perm.WORKFLOW_ADD,
  },
  {
    to: generatePath(routes.private.contact, { id: 'new', contactParty: ContactParty.vendor }),
    label: i18n.t('common:navigation.vendor'),
    feature: FeatureKey.vendors,
    icon: IconBuildingWarehouse,
    // CREATE quick-action: backend POST on Collaboration is gated on
    // accounting.manage (PerMethodPerms in accounting/views/
    // collaboration.py). Surfacing this for users with only
    // vendor.view would 403 on submit.
    requirePerm: Perm.ACCOUNTING_MANAGE,
  },
  {
    to: generatePath(routes.private.contact, { id: 'new', contactParty: ContactParty.customer }),
    label: i18n.t('common:navigation.customer'),
    feature: FeatureKey.customers,
    icon: IconUsers,
    requirePerm: Perm.ACCOUNTING_MANAGE,
  },
]

const openSuggestFeature = () => {
  // Intercom is the low-friction path; if the widget hasn't loaded, fall back to email.
  if (isIntercomAvailable()) {
    safeIntercom('showNewMessage', i18n.t('common:suggestFeature.intercomPrefill'))
  } else {
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      String(i18n.t('common:suggestFeature.emailSubject')),
    )}`
  }
}

export const useGetResourceItems = () => {
  // `t` from useTranslation is reactive to language switches (unlike the
  // module-level i18n.t used for the static nav arrays above), so the memo
  // recomputes and the menu labels stay in the active locale.
  const { t } = useTranslation('common', { keyPrefix: 'navigation' })
  return useMemo(
    () => [
      // First, and deliberately so: this menu is where people go when stuck,
      // and until now it offered only self-serve reading material while
      // actively hiding the chat widget on open (see HelpMenu).
      //
      // `href` rather than `onClick` for the fallback: the Teams build has no
      // Intercom, and a mailto pushed through window.location or react-router
      // Link is unreliable inside that webview. A real anchor hands it to the
      // OS mail client.
      isIntercomAvailable()
        ? { label: t('chatSupport'), onClick: openSupportChat }
        : {
            label: t('emailSupport'),
            href: `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(String(t('emailSupportSubject')))}`,
          },
      { to: GUIDES_LINK, label: t('allGuides'), external: true },
      { to: YOUTUBE_PLAYLISTS, label: t('videoGuides'), external: true },
      { to: CALL_LINK, label: t('bookCall'), external: true },
      { label: t('suggestFeature'), onClick: openSuggestFeature },
    ],
    [t],
  )
}
